import { getDoc, onSnapshot } from 'firebase/firestore';
import { getEffectiveTenantId, tenantPatientDoc } from './tenantContext';
import { previousYm, ymInOrgTimeZone } from '../utils/organizationalTime';

const BILLING_DATA_COLLECTION = 'billingData';

/**
 * RPM care minutes for a billingData doc. totalMinutes includes every program's
 * time; months reconciled before minutesByProgram existed were all RPM.
 * Mirrors functions/utils/cptRuleEvaluator.rpmMinutes.
 */
export function rpmMinutesFromBillingData(data) {
  const byProgram = data?.minutesByProgram;
  if (byProgram && typeof byProgram === 'object') return Number(byProgram.RPM) || 0;
  return data?.totalMinutes || 0;
}

/**
 * Derive the yyyy-mm key from an effectiveDate string (YYYY-MM-DD).
 * @param {string} effectiveDate
 * @returns {string} e.g. "2026-05"
 */
export function ymKeyFromEffectiveDate(effectiveDate) {
  if (!effectiveDate || effectiveDate.length < 7) return '';
  return effectiveDate.slice(0, 7);
}

/**
 * Current calendar month key.
 * @returns {string}
 */
export function currentYmKey() {
  return ymInOrgTimeZone();
}

/**
 * Previous calendar month key.
 * @returns {string}
 */
export function lastMonthYmKey() {
  return previousYm(ymInOrgTimeZone());
}

/**
 * Read the billing data doc for a single patient + month.
 * @param {string} patientId
 * @param {string} ymKey - e.g. "2026-05"
 * @returns {Promise<{totalMinutes: number, encounterCountTotal: number, encounterCounts: Record<string,number>} | null>}
 */
export async function getPatientMonthlyMinutes(patientId, ymKey) {
  if (!patientId || !ymKey) return null;
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return null;
  const ref = tenantPatientDoc(tenantId, patientId, BILLING_DATA_COLLECTION, ymKey);
  const snap = await getDoc(ref);
  if (!snap.exists()) return { totalMinutes: 0, encounterCountTotal: 0, encounterCounts: {} };
  const data = snap.data();
  return {
    totalMinutes: rpmMinutesFromBillingData(data),
    encounterCountTotal: data.encounterCountTotal || 0,
    encounterCounts: data.encounterCounts || {},
  };
}

/**
 * Max in-flight billing doc reads. Unbounded per-patient fan-outs (concurrent
 * with other dashboard queries) have saturated the Firestore WebChannel
 * transport before; keep the batch polite.
 */
const BILLING_READ_CONCURRENCY = 50;

/**
 * Batch-read monthly minutes for multiple patients (used by the Care Queue).
 * @param {string[]} patientIds
 * @param {string} ymKey
 * @returns {Promise<Map<string, {totalMinutes: number, encounterCountTotal: number, encounterCounts: Record<string,number>}>>}
 */
export async function batchGetMonthlyMinutes(patientIds, ymKey) {
  const result = new Map();
  if (!patientIds?.length || !ymKey) return result;
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return result;

  const readOne = async (pid) => {
    const ref = tenantPatientDoc(tenantId, pid, BILLING_DATA_COLLECTION, ymKey);
    try {
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        result.set(pid, {
          totalMinutes: rpmMinutesFromBillingData(data),
          encounterCountTotal: data.encounterCountTotal || 0,
          encounterCounts: data.encounterCounts || {},
          deviceReadingCount: data.deviceReadingCount || 0,
          manualReadingCount: data.manualReadingCount || 0,
          deviceReadingDates: Array.isArray(data.deviceReadingDates) ? data.deviceReadingDates : [],
          cptEvaluation: data.cptEvaluation || {},
        });
      } else {
        result.set(pid, { totalMinutes: 0, encounterCountTotal: 0, encounterCounts: {}, deviceReadingCount: 0, manualReadingCount: 0, deviceReadingDates: [], cptEvaluation: {} });
      }
    } catch {
      result.set(pid, { totalMinutes: 0, encounterCountTotal: 0, encounterCounts: {}, deviceReadingCount: 0, manualReadingCount: 0, deviceReadingDates: [], cptEvaluation: {} });
    }
  };

  for (let i = 0; i < patientIds.length; i += BILLING_READ_CONCURRENCY) {
    const slice = patientIds.slice(i, i + BILLING_READ_CONCURRENCY);
    await Promise.all(slice.map(readOne));
  }
  return result;
}

/**
 * Live-subscribe to a single patient's monthly billing doc (used by the patient
 * header). billingData totals are written exclusively by the server-side
 * reconcile Cloud Function, so a snapshot listener keeps the Care pill in sync
 * with encounter edits (and the async CPT re-evaluation) without polling.
 *
 * @param {string} patientId
 * @param {string} ymKey - e.g. "2026-08"
 * @param {(data: {totalMinutes: number, encounterCountTotal: number, encounterCounts: Record<string,number>, deviceReadingCount: number, manualReadingCount: number, deviceReadingDates: string[], cptEvaluation: Record<string,object>}) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeMonthlyMinutes(patientId, ymKey, callback) {
  if (!patientId || !ymKey) return () => {};
  let unsub = null;
  let cancelled = false;

  const empty = {
    totalMinutes: 0,
    encounterCountTotal: 0,
    encounterCounts: {},
    deviceReadingCount: 0,
    manualReadingCount: 0,
    deviceReadingDates: [],
    cptEvaluation: {},
  };

  (async () => {
    const tenantId = await getEffectiveTenantId();
    if (!tenantId || cancelled) return;
    const ref = tenantPatientDoc(tenantId, patientId, BILLING_DATA_COLLECTION, ymKey);
    unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          callback({ ...empty });
          return;
        }
        const data = snap.data();
        callback({
          totalMinutes: rpmMinutesFromBillingData(data),
          encounterCountTotal: data.encounterCountTotal || 0,
          encounterCounts: data.encounterCounts || {},
          deviceReadingCount: data.deviceReadingCount || 0,
          manualReadingCount: data.manualReadingCount || 0,
          deviceReadingDates: Array.isArray(data.deviceReadingDates) ? data.deviceReadingDates : [],
          cptEvaluation: data.cptEvaluation || {},
        });
      },
      () => {
        // Ignore listener errors; the header keeps its last known value.
      },
    );
  })();

  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}
